export interface LlmCitationAnnotation {
  type?: string;
  url?: string;
  title?: string;
  start_index?: number;
  end_index?: number;
  index?: number;
  file_id?: string;
  filename?: string;
  quote?: string;
  text?: string;
  searchResultText?: string;
  score?: number;
  attributes?: Record<string, unknown>;
}

const OPENAI_FILE_CITATION_MARKER_RE = /filecite[^]*/g;
const OPENAI_GENERIC_CITATION_MARKER_RE = /(?:file)?cite[^]*/g;
const LEGACY_SOURCE_MARKER_RE = /【[^】]*†source】/g;
const FILE_CITATION_HREF_PREFIX = '#llm-citation-';

export interface FormatLlmMessageCitationsOptions {
  linkFileCitations?: boolean;
}

export interface LlmCitationSource {
  number: number;
  type: 'url' | 'file';
  annotation: LlmCitationAnnotation;
  title: string;
  url?: string;
  snippet: string | null;
}

export function isLlmUrlCitation(annotation: LlmCitationAnnotation): annotation is LlmCitationAnnotation & {
  type: 'url_citation';
  url: string;
  start_index: number;
  end_index: number;
} {
  return annotation.type === 'url_citation'
    && typeof annotation.url === 'string'
    && typeof annotation.start_index === 'number'
    && typeof annotation.end_index === 'number';
}

export function getLlmAnnotationText(
  annotation: LlmCitationAnnotation,
  keys: string[],
): string | null {
  const record = annotation as Record<string, unknown>;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return null;
}

export function getLlmCitationSnippet(annotation: LlmCitationAnnotation): string | null {
  const direct = getLlmAnnotationText(annotation, ['quote', 'searchResultText', 'snippet', 'text']);
  if (direct) return normalizeLlmCitationText(direct);

  const record = annotation as Record<string, unknown>;
  const nestedText = getNestedCitationText(record.content) || getNestedCitationText(record.results);
  return nestedText ? normalizeLlmCitationText(nestedText) : null;
}

export function getLlmCitationTitle(annotation: LlmCitationAnnotation): string {
  if (isLlmUrlCitation(annotation)) {
    return annotation.title || getDomain(annotation.url);
  }
  return getLlmAnnotationText(annotation, ['filename', 'title', 'file_id'])
    || 'Документ хранилища';
}

export function getLlmCitationSources(
  annotations: LlmCitationAnnotation[] | null | undefined,
): LlmCitationSource[] {
  const safeAnnotations = annotations || [];
  const uniqueUrls = getUniqueLlmUrlCitations(safeAnnotations);
  const uniqueFiles = getUniqueLlmFileCitations(safeAnnotations);

  return [
    ...uniqueUrls.map((annotation, index) => ({
      number: index + 1,
      type: 'url' as const,
      annotation,
      title: getLlmCitationTitle(annotation),
      url: annotation.url,
      snippet: getLlmCitationSnippet(annotation),
    })),
    ...uniqueFiles.map((annotation, index) => ({
      number: uniqueUrls.length + index + 1,
      type: 'file' as const,
      annotation,
      title: getLlmCitationTitle(annotation),
      snippet: getLlmCitationSnippet(annotation),
    })),
  ];
}

export function getLlmFileCitationHref(number: number): string {
  return `${FILE_CITATION_HREF_PREFIX}${number}`;
}

export function getLlmFileCitationNumber(href: string | null | undefined): number | null {
  if (!href?.startsWith(FILE_CITATION_HREF_PREFIX)) return null;
  const value = Number.parseInt(href.slice(FILE_CITATION_HREF_PREFIX.length), 10);
  return Number.isFinite(value) && value > 0 ? value : null;
}

export function compactLlmCitationSnippet(snippet: string, maxLength = 180): string {
  const compact = snippet.replace(/\s+/g, ' ').trim();
  if (compact.length <= maxLength) return compact;
  return `${compact.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
}

export function getUniqueLlmUrlCitations(
  annotations: LlmCitationAnnotation[],
): Array<LlmCitationAnnotation & { type: 'url_citation'; url: string; start_index: number; end_index: number }> {
  const seen = new Set<string>();
  return annotations.filter(isLlmUrlCitation).filter((annotation) => {
    if (seen.has(annotation.url)) return false;
    seen.add(annotation.url);
    return true;
  });
}

export function getUniqueLlmFileCitations(
  annotations: LlmCitationAnnotation[],
): LlmCitationAnnotation[] {
  const seen = new Set<string>();
  return annotations.filter((annotation) => !isLlmUrlCitation(annotation)).filter((annotation) => {
    const key = getFileCitationKey(annotation);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function formatLlmMessageCitations(
  content: string,
  annotations: LlmCitationAnnotation[] | null | undefined,
  options: FormatLlmMessageCitationsOptions = {},
): string {
  const safeAnnotations = annotations || [];
  if (safeAnnotations.length === 0) return formatStreamingLlmCitations(content);

  const uniqueUrls = getUniqueLlmUrlCitations(safeAnnotations);
  const uniqueFiles = getUniqueLlmFileCitations(safeAnnotations);
  const fileNumberByKey = new Map<string, number>();
  uniqueFiles.forEach((annotation, index) => {
    fileNumberByKey.set(getFileCitationKey(annotation), uniqueUrls.length + index + 1);
  });

  let result = applyIndexedCitationAnnotations(content, safeAnnotations, uniqueUrls, fileNumberByKey, options);
  let fileOccurrence = 0;
  const fileAnnotations = safeAnnotations.filter((annotation) => !isLlmUrlCitation(annotation));
  result = result.replace(OPENAI_FILE_CITATION_MARKER_RE, () => {
    const annotation = fileAnnotations[fileOccurrence] || fileAnnotations[fileAnnotations.length - 1];
    fileOccurrence += 1;
    if (!annotation) return '[источник]';
    return formatFileCitationLabel(
      fileNumberByKey.get(getFileCitationKey(annotation)) || uniqueUrls.length + 1,
      options,
    );
  });

  return formatStreamingLlmCitations(result);
}

export function formatStreamingLlmCitations(content: string): string {
  return content
    .replace(OPENAI_FILE_CITATION_MARKER_RE, '[источник]')
    .replace(OPENAI_GENERIC_CITATION_MARKER_RE, '[источник]')
    .replace(LEGACY_SOURCE_MARKER_RE, '[источник]');
}

function applyIndexedCitationAnnotations(
  content: string,
  annotations: LlmCitationAnnotation[],
  uniqueUrls: Array<LlmCitationAnnotation & { type: 'url_citation'; url: string; start_index: number; end_index: number }>,
  fileNumberByKey: Map<string, number>,
  options: FormatLlmMessageCitationsOptions,
): string {
  const replacements: Array<{ start: number; end: number; value: string }> = [];
  const hasInlineFileMarkers = content.match(OPENAI_FILE_CITATION_MARKER_RE) !== null;
  OPENAI_FILE_CITATION_MARKER_RE.lastIndex = 0;

  for (const annotation of annotations.filter(isLlmUrlCitation)) {
    replacements.push({
      start: annotation.start_index,
      end: annotation.end_index,
      value: `[${uniqueUrls.findIndex((item) => item.url === annotation.url) + 1} · ${getDomain(annotation.url)}](${annotation.url})`,
    });
  }

  if (!hasInlineFileMarkers) {
    const inserted = new Set<string>();
    for (const annotation of annotations.filter((item) => !isLlmUrlCitation(item))) {
      if (typeof annotation.index !== 'number') continue;
      const number = fileNumberByKey.get(getFileCitationKey(annotation));
      if (!number) continue;
      const key = `${annotation.index}:${number}`;
      if (inserted.has(key)) continue;
      inserted.add(key);
      replacements.push({
        start: annotation.index,
        end: annotation.index,
        value: ` ${formatFileCitationLabel(number, options)}`,
      });
    }
  }

  if (replacements.length === 0) return content;

  return replacements
    .sort((a, b) => b.start - a.start || b.end - a.end)
    .reduce((current, replacement) => {
      if (
        replacement.start < 0
        || replacement.end < replacement.start
        || replacement.start > current.length
        || replacement.end > current.length
      ) {
        return current;
      }
      return `${current.slice(0, replacement.start)}${replacement.value}${current.slice(replacement.end)}`;
    }, content);
}

function getFileCitationKey(annotation: LlmCitationAnnotation): string {
  const fileId = getLlmAnnotationText(annotation, ['file_id']);
  const filename = getLlmAnnotationText(annotation, ['filename']);
  const snippet = getLlmCitationSnippet(annotation);
  if (fileId || filename || snippet) {
    return [fileId, filename, snippet].filter(Boolean).join('::');
  }
  return JSON.stringify(annotation);
}

function getDomain(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function formatFileCitationLabel(
  number: number,
  options: FormatLlmMessageCitationsOptions,
): string {
  return options.linkFileCitations
    ? `[${number}](${getLlmFileCitationHref(number)})`
    : `[${number}]`;
}

function normalizeLlmCitationText(text: string): string {
  return text
    .replace(/\r\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function getNestedCitationText(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value;
  if (!Array.isArray(value)) return null;

  const parts = value
    .map((item) => {
      if (typeof item === 'string') return item;
      if (!item || typeof item !== 'object') return null;
      const record = item as Record<string, unknown>;
      if (typeof record.text === 'string') return record.text;
      if (typeof record.content === 'string') return record.content;
      return getNestedCitationText(record.content);
    })
    .filter((item): item is string => typeof item === 'string' && item.trim().length > 0);

  return parts.length > 0 ? parts.join('\n\n') : null;
}
